'use client'

import { useState } from 'react'
import { useFavorites } from '@/contexts/FavoritesContext'
import { elements } from '@/data/elements'
import { ElementCard } from '@/components/ElementCard'
import { ElementDialog } from '@/components/ElementDialog'
import { Button } from '@/components/ui/button'
import { Heart, Trash2 } from 'lucide-react'
import { useTranslations } from 'next-intl'

export default function FavoritesPage() {
  const { favorites, clearFavorites } = useFavorites()
  const [selectedElement, setSelectedElement] = useState<typeof elements[0] | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const t = useTranslations('Favorites')

  const favoriteElements = elements.filter(el => favorites.includes(el.id.toString()))

  const handleElementClick = (element: typeof elements[0]) => {
    setSelectedElement(element)
    setDialogOpen(true)
  }

  return (
    <div className="min-h-screen bg-[#0a192f] py-20 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">{t('title')}</h1>
          <p className="text-[#a8b2d1]">{t('subtitle')}</p>
        </div>

        {favorites.length > 0 && (
          <div className="mb-6">
            <Button
              onClick={() => {
                if (confirm(t('confirmClear'))) {
                  clearFavorites()
                }
              }}
              variant="outline"
              className="border-red-500 text-red-500 hover:bg-red-500/10"
            >
              <Trash2 size={16} className="mr-2" />
              {t('clearAll')}
            </Button>
          </div>
        )}

        {favoriteElements.length === 0 ? (
          <div className="text-center py-20">
            <Heart size={64} className="mx-auto text-[#233554] mb-4" />
            <h2 className="text-2xl font-semibold text-white mb-2">
              {t('noFavorites')}
            </h2>
            <p className="text-[#a8b2d1]">
              {t('noFavoritesDesc')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {favoriteElements.map(element => (
              <ElementCard
                key={element.id}
                element={element}
                onClick={() => handleElementClick(element)}
              />
            ))}
          </div>
        )}
      </div>

      <ElementDialog
        element={selectedElement}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
      />
    </div>
  )
}
